const pad2 = (n) => String(n).padStart(2, '0');

/** API save format: HH:mm:ss.sssZ */
export function formatIntakeTimeForApi(timeStr) {
  if (!timeStr) return null;
  if (timeStr.includes('Z') && timeStr.includes('.')) return timeStr;
  if (timeStr.includes('Z') && !timeStr.includes('.')) {
    const parts = timeStr.replace('Z', '').split(':');
    if (parts.length === 3) {
      return `${parts[0]}:${parts[1]}:${parts[2]}.000Z`;
    }
  }
  const parts = String(timeStr).split(':');
  if (parts.length === 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00.000Z`;
  }
  if (parts.length === 3) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${parts[2].padStart(2, '0')}.000Z`;
  }
  return `${timeStr}:00.000Z`;
}

/** UI input/display: HH:mm from API or date string. */
export function parseIntakeTimeForDisplay(timeStr) {
  if (!timeStr) return '';
  if (timeStr.includes(':')) {
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
  }
  const d = new Date(timeStr);
  if (!isNaN(d.getTime())) {
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }
  return '';
}

/** UI save: HH:mm → HH:mm:ss.sssZ */
export function formatTimeForSave(hhmm) {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map(Number);
  return `${pad2(h || 0)}:${pad2(m || 0)}:00.000Z`;
}

/*
 * Meal intake windows (intake_start_time / intake_end_time) are wall-clock
 * times of day, shown and saved as-is with no timezone conversion. The API
 * returns "HH:MM:SS+00:00" or "HH:MM:SSZ" (null = no window) and accepts
 * "HH:MM" / "HH:MM:SS" without an offset; the suffix is ignored.
 */

const parseHHMM = (timeStr) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(timeStr || '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return { h, min };
};

/** API time ("08:00:00+00:00", "08:00:00Z", "08:00", null) → "HH:MM" or "". */
export function apiTimeToHHMM(apiTime) {
  const t = parseHHMM(apiTime);
  return t ? `${pad2(t.h)}:${pad2(t.min)}` : '';
}

/** UI "HH:MM" → "HH:MM" for the API; "" (cleared) stays "". */
export function hhmmForApi(hhmm) {
  return apiTimeToHHMM(hhmm);
}

/**
 * A meal's intake window in "HH:MM" ("" = not set). Reads the plain
 * names (meal types, target plan and tracked meals) and falls back to the
 * template-response `meal_type_` prefixed names.
 * @returns {{ start: string, end: string }}
 */
export function getMealIntakeWindow(meal) {
  if (!meal) return { start: '', end: '' };
  return {
    start: apiTimeToHHMM(meal.intake_start_time ?? meal.meal_type_intake_start_time),
    end: apiTimeToHHMM(meal.intake_end_time ?? meal.meal_type_intake_end_time),
  };
}

/** "08:00–09:30", "From 08:00", "Until 09:30" or "" for no window. */
export function formatIntakeWindowLabel(window) {
  const start = window?.start || '';
  const end = window?.end || '';
  if (start && end) return `${start}–${end}`;
  if (start) return `From ${start}`;
  if (end) return `Until ${end}`;
  return '';
}

/** PDF display: 12-hour clock with AM/PM. */
export function formatIntakeTimeForDisplay(timeStr) {
  if (!timeStr) return '';
  if (timeStr.includes(':')) {
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      const hour = parseInt(parts[0], 10);
      const minute = parts[1].substring(0, 2);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minute} ${period}`;
    }
  }
  return timeStr;
}

/** @deprecated alias */
export const formatIntakeTime = formatIntakeTimeForApi;
